import { useMutation } from "@apollo/client/react";
import { ShieldCheck } from "lucide-react";
import posthog from "posthog-js";
import { useEffect, useRef, useState } from "react";
import { b } from "@/b";
import { klaviyoIdentify } from "@/util/klaviyo";
import { AuthErrorCode, CLIENT_MAX_CODE_ATTEMPTS, getAuthErrorCode } from "../auth-errors";

const VERIFY_TOTP_MUTATION = b.mutation("VerifyTOTP", { token: "String!" }, (b, v) => [
  b.viewer((b) => [
    b.completeTOTPFlow({ token: v.token }, (b) => [
      b.id(),
      b.account((b) => [b.id(), b.firstName(), b.lastName(), b.phone(), b.email()]),
      b.employee((b) => [b.id()]),
    ]),
  ]),
]);

export const EnterCodeStep: React.FC<{
  onComplete: () => void;
  onRequestRestart?: () => void;
}> = ({ onComplete, onRequestRestart }) => {
  const [token, setToken] = useState("");
  // Tracks the last token we actually submitted so we don't re-fire on
  // re-edits of the same digits or while the same wrong code is on screen.
  const lastSubmittedRef = useRef<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [errorCode, setErrorCode] = useState<AuthErrorCode | null>(null);
  const exhausted =
    attempts >= CLIENT_MAX_CODE_ATTEMPTS || errorCode === AuthErrorCode.RATE_LIMITED;

  const [verifyTOTP, { loading, error }] = useMutation(VERIFY_TOTP_MUTATION.document(), {
    onCompleted: (data) => {
      const account = data?.viewer?.completeTOTPFlow?.account;
      if (account?.id) {
        posthog.identify(account.id, {
          firstName: account.firstName,
          lastName: account.lastName,
        });
        // Stitch the anonymous Klaviyo onsite cookie to this profile (§4.4).
        // Unprefixed keys only — phone_number carries the stitch under phone-first
        // auth; external_id stays server-side (KlaviyoProfileService).
        klaviyoIdentify({
          phone_number: account.phone,
          email: account.email,
          first_name: account.firstName,
          last_name: account.lastName,
        });
      }
      posthog.capture("sign_in_completed");
      onComplete();
    },
  });

  const submit = (value: string) => {
    if (loading || exhausted || value.length !== 6) return;
    if (lastSubmittedRef.current === value) return;
    lastSubmittedRef.current = value;
    setAttempts((a) => a + 1);
    setErrorCode(null);
    verifyTOTP({ variables: { token: value } }).catch((err: unknown) => {
      setErrorCode(getAuthErrorCode(err) ?? AuthErrorCode.INVALID_CODE);
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: submit is stable per render; we only want to react to token changes
  useEffect(() => {
    if (token.length === 6) submit(token);
  }, [token]);

  const message =
    errorCode === AuthErrorCode.RATE_LIMITED
      ? "Too many incorrect codes. Please request a new code."
      : errorCode === AuthErrorCode.VERIFICATION_EXPIRED
        ? "This code has expired. Please request a new one."
        : errorCode === AuthErrorCode.NO_ACTIVE_VERIFICATION
          ? "Your sign-in session expired. Please start again."
          : errorCode === AuthErrorCode.INVALID_CODE
            ? "That code didn't match. Try again."
            : error?.message;

  return (
    <div className="text-center space-y-5">
      <div className="flex items-center justify-center size-14 rounded-2xl bg-brand-mint/15 mx-auto">
        <ShieldCheck className="size-6 text-gray-700" />
      </div>
      <div>
        <h2 className="text-xl font-bold text-brand-black">Verify Your Phone</h2>
        <p className="text-sm text-gray-500 mt-1">Enter the 6-digit code we sent to your phone</p>
      </div>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="000000"
        maxLength={6}
        value={token}
        disabled={exhausted}
        onChange={(e) => setToken(e.target.value.replace(/\D/g, ""))}
        className="w-full max-w-[180px] mx-auto block px-4 py-3 rounded-xl border border-gray-200 text-center text-2xl font-bold tracking-[0.3em] text-brand-black placeholder:text-gray-300 outline-none focus:border-brand-mint focus:ring-2 focus:ring-brand-mint/30 transition-all disabled:opacity-50"
      />
      {message && <p className="text-sm text-red-500">{message}</p>}
      {exhausted && onRequestRestart ? (
        <button
          type="button"
          onClick={onRequestRestart}
          className="w-full py-3 rounded-xl text-sm font-semibold bg-brand-mint text-brand-black hover:bg-brand-mint/90 transition-all cursor-pointer active:scale-[0.98]"
        >
          Start over
        </button>
      ) : (
        <button
          type="button"
          onClick={() => submit(token)}
          disabled={loading || token.length < 6 || exhausted}
          className="w-full py-3 rounded-xl text-sm font-semibold bg-brand-mint text-brand-black hover:bg-brand-mint/90 transition-all cursor-pointer active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {loading ? "Verifying..." : "Submit"}
        </button>
      )}
    </div>
  );
};
